import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import { isPlatformServer, Location } from '@angular/common';
import { Router } from '@angular/router';
import { Auth, Hub } from 'aws-amplify';
import {filter, map, switchMap, take, delay, catchError} from 'rxjs/operators';
import {
	BehaviorSubject,
	combineLatest,
	EMPTY,
	from,
	Observable,
	of,
	Subject,
	takeUntil,
	tap,
	withLatestFrom
} from 'rxjs';


import {BaseAccount} from "~modules/auth/types/account.types";
import {WebsocketService} from "~core/services/websockets.service";
import { UserType } from '~shared/enums/user.enum';
import { LogoutReason } from '~modules/auth/auth.const';

import { InternalUser, ExternalUser, CognitoUser } from '../../modules/auth/types/user.types';

import { AccountService } from './account.service';
import { AlertService } from './alert.service';
import { RouteDataService } from './route-data.service';

@Injectable()
export class AuthService implements  OnDestroy {

	private cognitoUser$: BehaviorSubject<CognitoUser> = new BehaviorSubject(null);

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public accountIdOverwrite$: BehaviorSubject<string> = new BehaviorSubject(typeof localStorage !== 'undefined' ? localStorage.getItem('accountIdOverwrite') || null : null);
	public automaticOverwrite = false;
	public currentUser$: BehaviorSubject<InternalUser | ExternalUser> = new BehaviorSubject(null);
	public userId: string;
	public accountId: string;
	public jwtToken: string;
	public cognitoUserState = new BehaviorSubject<'ready' | 'loading'>('ready');
	public cognitoUserState$ = this.cognitoUserState.asObservable();
	private unsubscribe$ = new Subject<void>();


	constructor(
		private readonly router: Router,
		private readonly location: Location,
		private readonly accountService: AccountService,
		private readonly ws: WebsocketService,
		private readonly routeDataService: RouteDataService,
		private readonly alertService: AlertService,
		@Inject(PLATFORM_ID) private readonly platformId: object
	) {
		Hub.listen('auth', async (data) => {
			switch (data.payload.event) {
			case 'signIn':
			case 'autoSignIn':
				this.cognitoUser$.next(await this.getCognitoUser());
				break;
			}
		});

		this.initCognitoUser();

		this.cognitoUser$
			.pipe(
				filter((cognitoUser) => !!cognitoUser),
				takeUntil(this.unsubscribe$),
			).subscribe((cognitoUser) => {
				this.jwtToken = cognitoUser.signInUserSession.idToken.jwtToken;
				this.userId = cognitoUser.attributes.email;
			    this.cognitoUserState.next('ready');

			    this.handleCognitoUser(cognitoUser)
			});

		this.setupWebSocketManagement();
		this.handleWebSocketReconnection();
	}

	/**
	 * Manages WebSocket connection based on user state and route data.
	 * Connects when user is authenticated/registered and route allows WebSocket.
	 * Disconnects when navigating to a route that skips WebSocket.
	 */
	private setupWebSocketManagement(): void {
		combineLatest([
			this.currentUser$,
			this.routeDataService.data$,
		]).pipe(withLatestFrom(this.ws.wsConnectionState$),
			takeUntil(this.unsubscribe$),
			catchError((err) => {
				console.error('SetupWebSocketManagement error', err);
				return of(null);
			})
		).subscribe(([[user, routeData], connectionState]
		) => {
			const isLoginPage = routeData.url?.includes('/auth/login');
			if (isLoginPage && !user) {
				return;
			}
			const shouldConnect = user?.registered && this.jwtToken && !routeData.skipWebSocket;
			const isConnectedOrConnecting = connectionState === 'connected' || connectionState === 'connecting';
			if (shouldConnect && !isConnectedOrConnecting) {
				this.ws.connect(`${this.jwtToken}`);
			} else if (routeData.skipWebSocket && connectionState === 'connected') {
				this.ws.close();
			}
		});
	}

	private handleWebSocketReconnection(): void {
		this.ws.onFirstReconnectAttempt$.pipe(
			switchMap(() => from(this.refreshToken())),
			filter(success => success),
			takeUntil(this.unsubscribe$)
		).subscribe(() => {
			if (this.jwtToken) {
				this.ws.connect(this.jwtToken);
			}
		});
	}


	public handleCognitoUser(cognitoUser, redirectTo?: string): void {
		this.loading$.next(true);
		this.accountService.getUser(cognitoUser.attributes.email)
			.pipe(
				take(1),
				delay(0),
				catchError((error) => {
					this.loading$.next(false);
					if (error?.status === 401) {
						this.logout(this.location.path(), 'session-expired');
					} else if (error?.status === 404) {
						// Signed in, but there is no user behind the login: nothing in the
						// app can work, so say so on the login page instead of waiting forever.
						this.logout(undefined, 'no-account');
					} else {
						this.alertService.handleError(error);
					}
					return EMPTY;
				}),
				switchMap((currentUser) => {
					return this.accountIdOverwrite$.pipe(
						tap(() => this.loading$.next(true)),
						map((accountIdOverwrite) => ({ accountIdOverwrite, currentUser }))
					)
				}),
				switchMap(({ currentUser, accountIdOverwrite }) => {
					// The account overwrite is an INTERNAL-user feature (the
					// account-switcher, rendered only for ADMIN/SALES). An external
					// user's account always comes from their own user record — never
					// from a persisted overwrite, which may be stale from a previous
					// session on this browser. Applying it would send a foreign
					// account_id under this user's JWT and the backend rejects it with
					// "passed account id was not the account id that belonged to the
					// user". Discard any stale overwrite for non-internal users.
					if (accountIdOverwrite && currentUser?.userType !== UserType.INTERNAL) {
						this.overwriteAccountId(null);
						return of({ accountIdOverwrite: null, currentUser })
					}

					if (!accountIdOverwrite) {
						return of({ accountIdOverwrite, currentUser })
					}

					return this.accountService.getAccount(accountIdOverwrite)
						.pipe(
							catchError(() => {
								localStorage.removeItem('accountIdOverwrite');
								this.accountIdOverwrite$.next(null);
								return EMPTY;
							}),
							map((account: BaseAccount) => ({
								currentUser: {
									...currentUser,
									account: {
										...account,
										overwritten: true
									},
									accountId: account.id
								},
								accountIdOverwrite
							})))
				})
			)
			.subscribe(
				{
					next: ({ currentUser, accountIdOverwrite }) => {
						this.currentUser$.next(currentUser);
						this.loading$.next(false);
						this.accountId = (currentUser as ExternalUser)?.account?.id || accountIdOverwrite || null;

						if (!currentUser) {
							return;
						}

						if (currentUser.registered || this.location.path().includes('/auth/complete-user')) {
							return;
						}

						this.router.navigate(['/', 'auth', 'complete-user'], {
							queryParams: {
								redirectTo: this.location.path()
							}
						});
					}
				});
	}

	async initCognitoUser() {
		// A Cognito session lives in browser storage, so on the server there is
		// never one to find. Amplify is also left unconfigured there (see the
		// APP_INITIALIZER in AppModule), so calling Auth would only log an
		// AuthError and throw.
		if (isPlatformServer(this.platformId)) {
			this.cognitoUserState.next('ready');
			return;
		}

		this.cognitoUserState.next('loading');
		try {
			const user = await Auth.currentAuthenticatedUser();
			this.cognitoUser$.next(user);
		} catch {
			try {
				// Cached user not found — try refreshing the session via the refresh token
				await Auth.currentSession();
				const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
				this.cognitoUser$.next(user);
			} catch {
				const locationPath = this.location.path();
				// Match against the pathname only — never the query string. The
				// redirectTo param carries an encoded copy of the original target
				// (e.g. ?redirectTo=%2Fprojects%2F...), so a naive includes() over
				// the full path treats the unprotected /auth/login page as protected
				// and logs out again, re-encoding the URL and nesting redirectTo on
				// every cycle (infinite %25... loop). Only the real path decides.
				const pathname = locationPath.split('?')[0];
				const protectedPaths = ['projects', 'users', 'materials', 'core', 'maintenance-admin'];
				if (protectedPaths.some(path => pathname.includes(path))) {
					// Amplify keeps the last user's name next to the tokens: when it is
					// there, a session existed and ran out, rather than none at all.
					const hadSession = Object.keys(localStorage).some((key) => key.endsWith('.LastAuthUser'));
					await this.logout(locationPath, hadSession ? 'session-expired' : undefined);
				}
				this.cognitoUserState.next('ready');
			}
		}
	}

	public overwriteAccountId(accountId: string, automaticOverwrite = false): void {
		this.accountIdOverwrite$.next(accountId);
		this.automaticOverwrite = automaticOverwrite;

		if (accountId) {
			return localStorage.setItem('accountIdOverwrite', accountId);
		}

		localStorage.removeItem('accountIdOverwrite')
	}

	public reloadCognitoUser(bypassCache = false): Observable<CognitoUser> {
		return from(this.getCognitoUser(bypassCache))
			.pipe(
				tap((cognitoUser) => this.cognitoUser$.next(cognitoUser))
			)
	}

	private async getCognitoUser(bypassCache = false): Promise<CognitoUser> {
		return Auth.currentAuthenticatedUser({
			bypassCache
		});
	}

	async refreshToken(): Promise<boolean> {
		try {
			const session = await Auth.currentSession();
			this.jwtToken = session.getIdToken().getJwtToken();
			return true;
		} catch (err) {
			console.error('Session refresh failed:', err);
			// Offline the session may still be valid: the socket keeps retrying with the current token.
			// Any other failure means the session is gone, the same case as a failed refresh on an API call.
			if (err?.code !== 'NetworkError') {
				await this.logout(this.location.path(), 'session-expired');
			}
			return false;
		}
	}

	/** reason: why the user is sent to the login page, which tells them (see LOGOUT_REASONS). */
	async logout(redirectTo?: string, reason?: LogoutReason): Promise<void> {
		// Never carry an auth page as the redirect target — login is where we're
		// already going, and re-encoding it would nest redirectTo on every logout.
		const safeRedirectTo = redirectTo && !redirectTo.split('?')[0].includes('/auth/') ? redirectTo : undefined;
		const params = new URLSearchParams();
		if (safeRedirectTo) {
			params.set('redirectTo', safeRedirectTo);
		}
		if (reason) {
			params.set('reason', reason);
		}
		const query = params.toString();
		await Auth.signOut()
			.then(() => {
				localStorage.clear();
				this.cognitoUser$.next(null);

				this.currentUser$.next(null);
				this.ws.close();
				window.location.href = `/auth/login${query ? `?${query}` : ''}`
			});
	}

	/**
	 * Global Cognito sign-out (revokes tokens) for a non-admin user when
	 * maintenance is switched on, landing them on the public maintenance page
	 * rather than the login screen.
	 */
	async forceMaintenanceLogout(): Promise<void> {
		try {
			await Auth.signOut({ global: true });
		} catch {
			// Token may already be invalid — clear locally regardless.
		}
		localStorage.clear();
		this.cognitoUser$.next(null);
		this.currentUser$.next(null);
		this.ws.close();
		window.location.href = '/maintenance';
	}

	ngOnDestroy(): void {
		this.ws.close();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}
}
